// FloatingReportButton.js
// Global floating button mounted on every Wigley page (except the Feedback page itself).
// Two modes:
//   1. "Report this page"  — captures URL + active page name + viewport screenshot via
//                            html2canvas, then opens the Feedback / New Submission tab
//                            with the capture pre-filled.
//   2. "New submission"    — opens the Feedback / New Submission tab blank, no capture.
//
// Calls onReportRequested({ url, page, screenshotBlob, screenshotFilename, section }) which
// the parent (StaffPortal) routes by switching activePage to 'feedback' and passing the
// prefill object into <Feedback initialPrefill={...} />.

import React, { useState, useEffect } from 'react';

// ---------------------------------------------------------------------------
// Lazy load html2canvas from CDN once, cache the promise
// ---------------------------------------------------------------------------

let _h2cPromise = null;
function loadHtml2Canvas() {
  if (_h2cPromise) return _h2cPromise;
  if (window.html2canvas) {
    _h2cPromise = Promise.resolve(window.html2canvas);
    return _h2cPromise;
  }
  _h2cPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
    s.onload = () => resolve(window.html2canvas);
    s.onerror = () => reject(new Error('Failed to load html2canvas'));
    document.head.appendChild(s);
  });
  return _h2cPromise;
}

// ---------------------------------------------------------------------------
// Section name lookup — maps activePage id (StaffPortal) to display section
// ---------------------------------------------------------------------------

const PAGE_TO_SECTION = {
  dashboard:    'NTRG Dashboards',
  clients:      'Clients Screen',
  properties:   'Properties',
  parcels:      'Parcels Screen',
  consultants:  'Consultants Screen',
  activity:     'Reports & Activity Screen',
  intake:       'Document Intake',
  exchange:     'File Exchange',
  scanner:      'Document Scanner',
  users:        'Manage Users Screen',
  tasks:        'Task Engine Screen',
  extractor:    'Notice Extractor Screen',
  audit:        'Notice Audit',
  documents:    'Documents',
  admin:        'Admin Console Screen',
  feedback:     'Feedback',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function FloatingReportButton({ darkMode, activePage, onReportRequested, onOpenFeedback, hidden }) {
  const [open, setOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);

  // Close menu on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  // Close menu on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!e.target.closest('[data-floating-report]')) setOpen(false);
    };
    setTimeout(() => document.addEventListener('click', handler), 0);
    return () => document.removeEventListener('click', handler);
  }, [open]);

  if (hidden) return null;

  const isDark = !!darkMode;
  const sectionGuess = PAGE_TO_SECTION[activePage] || '';

  async function handleReportThisPage() {
    setOpen(false);
    setCapturing(true);
    try {
      const h2c = await loadHtml2Canvas();
      // Capture viewport at current scroll
      const canvas = await h2c(document.body, {
        backgroundColor: isDark ? '#0f1729' : 'var(--bg)',
        scale: 1,
        logging: false,
        useCORS: true,
        allowTaint: true,
        windowWidth: document.documentElement.clientWidth,
        windowHeight: document.documentElement.clientHeight,
        x: window.scrollX,
        y: window.scrollY,
        width: document.documentElement.clientWidth,
        height: document.documentElement.clientHeight,
      });
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      const filename = `${activePage || 'page'}_${new Date().toISOString().replace(/[:.]/g, '-')}.png`;

      onReportRequested({
        url: window.location.href,
        page: sectionGuess || activePage || '',
        section: sectionGuess || '',
        screenshotBlob: blob,
        screenshotFilename: filename,
      });
    } catch (e) {
      console.error('Screenshot failed:', e);
      // Fall through with no screenshot, just URL/page
      onReportRequested({
        url: window.location.href,
        page: sectionGuess || activePage || '',
        section: sectionGuess || '',
        screenshotBlob: null,
        screenshotFilename: null,
      });
    } finally {
      setCapturing(false);
    }
  }

  function handleNewSubmission() {
    setOpen(false);
    onReportRequested({
      url: null,
      page: null,
      section: '',
      screenshotBlob: null,
      screenshotFilename: null,
    });
  }

  function handleViewFeedback() {
    setOpen(false);
    onOpenFeedback();
  }

  return (
    <div data-floating-report style={{
      position:'fixed', bottom:'18px', right:'18px', zIndex:9999,
      display:'flex', alignItems:'flex-end', gap:'10px',
    }}>

      {/* Menu */}
      {open && (
        <div style={{
          background:'var(--ink)', color:'white', borderRadius:'8px', padding:'6px',
          minWidth:'240px', border:'1px solid rgba(180,0,0,0.4)',
          boxShadow:'0 8px 24px rgba(0,0,0,0.3)',
          display:'flex', flexDirection:'column', gap:'2px',
        }}>
          <button onClick={handleReportThisPage} style={menuItemStyle()}>
            <span style={{ fontSize:'16px' }}>⚑</span>
            <div style={{ flex:1, textAlign:'left' }}>
              <div style={{ fontWeight:600, fontSize:'13px' }}>Report this page</div>
              <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.6)' }}>Auto-capture URL + screenshot</div>
            </div>
          </button>
          <button onClick={handleNewSubmission} style={menuItemStyle()}>
            <span style={{ fontSize:'16px' }}>＋</span>
            <div style={{ flex:1, textAlign:'left' }}>
              <div style={{ fontWeight:600, fontSize:'13px' }}>New submission</div>
              <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.6)' }}>Just type — no capture</div>
            </div>
          </button>
          <button onClick={handleViewFeedback} style={menuItemStyle()}>
            <span style={{ fontSize:'16px' }}>📋</span>
            <div style={{ flex:1, textAlign:'left' }}>
              <div style={{ fontWeight:600, fontSize:'13px' }}>View all feedback</div>
              <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.6)' }}>Open the submissions list</div>
            </div>
          </button>
        </div>
      )}

      {/* Floating circle */}
      <button onClick={() => setOpen(!open)} disabled={capturing}
        title={capturing ? 'Capturing…' : 'Submit feedback'}
        style={{
          width:'48px', height:'48px', borderRadius:'50%',
          background: capturing ? '#9ca3af' : 'var(--red)',
          color:'white', display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:'22px', border:'none', cursor: capturing ? 'wait' : 'pointer',
          boxShadow:'0 4px 12px rgba(0,0,0,0.25)',
          transition:'transform 0.1s ease',
        }}>
        {capturing ? '…' : '⚑'}
      </button>
    </div>
  );
}

function menuItemStyle() {
  return {
    padding:'8px 12px', borderRadius:'6px', display:'flex', alignItems:'center', gap:'10px',
    background:'transparent', color:'white', border:'none', cursor:'pointer',
    fontFamily:"'Public Sans',system-ui,sans-serif", textAlign:'left', width:'100%',
  };
}
